import { toast } from "@/components/ui/toast";
import { useContext, useEffect, useMemo } from "react";
import { UserContext, useOpportunityIndexCampaigns, usePitchesByManagerIdThisMonth, useRelevantDocs } from "../../services/firebase";
import type { Client, User } from "../../shared/types";
import { canBrowseOpportunities } from "../../utils/pitching/permissions";
import { opportunityVisibleTo } from "./list";

/** Index campaigns this user can see, sector badges, and a manager’s this-month pitches for the stats bar. */
export function useOpportunities() {
    const user = useContext(UserContext);
    const isManager = Boolean(user?.roles?.manager);
    const enabled = canBrowseOpportunities(user);
    const { openCampaigns, closedCampaigns, loading, error } = useOpportunityIndexCampaigns(enabled);
    const pitchesQuery = usePitchesByManagerIdThisMonth(isManager ? user?.id : undefined);

    useEffect(() => {
        if (!error) {
            return;
        }
        toast.add({
            type: "error",
            title: "Couldn’t load opportunities",
            description: "Refresh and try again.",
        });
        console.error(error);
    }, [error]);

    useEffect(() => {
        if (!pitchesQuery.error) {
            return;
        }
        toast.add({
            type: "error",
            title: "Couldn’t load your pitches",
            description: "Refresh and try again.",
        });
        console.error(pitchesQuery.error);
    }, [pitchesQuery.error]);

    const visibleOpen = useMemo(
        () => openCampaigns.filter((campaign) => opportunityVisibleTo(user, campaign)),
        [openCampaigns, user],
    );
    const visibleClosed = useMemo(
        () => closedCampaigns.filter((campaign) => opportunityVisibleTo(user, campaign)),
        [closedCampaigns, user],
    );

    const bookerUids = useMemo(() => {
        const ids = [...visibleOpen, ...visibleClosed]
            .map((campaign) => campaign.booker?.uid)
            .filter((id): id is string => Boolean(id));
        return [...new Set(ids)].sort();
    }, [visibleClosed, visibleOpen]);
    const { data: bookers } = useRelevantDocs<User>({
        collectionName: "users",
        docIds: bookerUids,
        enabled: bookerUids.length > 0,
    });
    const clientIds = useMemo(() => {
        const ids = (bookers ?? []).map((booker) => booker.clientId).filter((id): id is string => Boolean(id));
        return [...new Set(ids)].sort();
    }, [bookers]);
    const { data: clients } = useRelevantDocs<Client>({
        collectionName: "clients",
        docIds: clientIds,
        enabled: clientIds.length > 0,
    });

    const sectorByBookerUid = useMemo(() => {
        const sectorByClient = new Map((clients ?? []).map((client) => [client.id, client.companySector]));
        const sectors = new Map<string, string>();
        for (const booker of bookers ?? []) {
            const sector = booker.clientId ? sectorByClient.get(booker.clientId) : undefined;
            if (booker.id && sector) {
                sectors.set(booker.id, sector);
            }
        }
        return sectors;
    }, [bookers, clients]);

    return {
        user,
        isManager,
        loading,
        pitches: pitchesQuery.pitches,
        pitchesLoading: pitchesQuery.loading,
        openCampaigns: visibleOpen,
        closedCampaigns: visibleClosed,
        sectorByBookerUid,
    };
}
